import { useNavigate } from "react-router-dom";
import { QueryParams } from "../../utils/types";
import { TableComponent } from "../table";
import { formatToCurrency, getMonthName } from "../../utils/helpers";
import { AdminRevenueTable } from "../../redux/features/payment/payment.types";

export const RevenueTable = ({
  fetchingRevenueData,
  revenueData,
  query,
  setQuery,
  clickable,
}: {
  fetchingRevenueData: boolean;
  revenueData?: AdminRevenueTable;
  query: QueryParams;
  setQuery: (query: QueryParams) => void;
  clickable?: boolean;
}) => {
  const navigate = useNavigate();
  const rows = revenueData?.data?.data ?? [];

  return (
    <div className="w-full">
      {fetchingRevenueData && (
        <div className="flex flex-col gap-2 md:hidden">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div
              key={`revenue-mobile-loader-${idx}`}
              className="h-[142px] animate-pulse rounded-2xl bg-grey-50"
            />
          ))}
        </div>
      )}

      {!fetchingRevenueData && rows.length === 0 && (
        <div className="rounded-2xl border border-grey-200 bg-white p-5 text-center md:hidden">
          <p className="text-base font-extrabold text-grey-950">No Data</p>
          <p className="mt-1 text-sm text-grey-600">
            Revenue summaries will show here once available.
          </p>
        </div>
      )}

      {!fetchingRevenueData && rows.length > 0 && (
        <div className="flex flex-col gap-2 md:hidden">
          {rows.map((entry) => {
            const monthLabel = `${getMonthName(entry.month)} ${entry.year}`;
            return (
              <button
                key={`${entry.month}-${entry.year}`}
                type="button"
                className="rounded-2xl border border-grey-200 bg-white p-3 text-left"
                onClick={() => {
                  if (clickable) {
                    navigate(`/overview/revenue/${entry.month}/${entry.year}`);
                  }
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-base font-extrabold text-grey-950">
                    {monthLabel}
                  </p>
                  <p className="rounded-full bg-grey-50 px-2 py-1 text-xs font-bold text-grey-700">
                    {entry.numberOfPaidEvents} paid events
                  </p>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <RevenueMobileMetric
                    label="Tickets Sold"
                    value={entry.totalPaidTickets.toLocaleString()}
                  />
                  <RevenueMobileMetric
                    label="Ticket Amount"
                    value={formatToCurrency(entry.amountProcessed ?? 0, {})}
                  />
                  <RevenueMobileMetric
                    label="Amount Paid"
                    value={formatToCurrency(entry.totalAmountPaid ?? 0, {})}
                  />
                  <RevenueMobileMetric
                    label="Revenue"
                    value={formatToCurrency(entry.revenue ?? 0, {})}
                  />
                  <RevenueMobileMetric
                    label="Profit"
                    value={formatToCurrency(entry.profit ?? 0, {})}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="hidden md:block">
        <TableComponent
          headers={[
            "Month/Year",
            <p className="text-right">Paid Events</p>,
            <p className="text-right">Paid Tickets Sold</p>,
            <p className="text-right">Paid Ticket Amount</p>,
            <p className="text-right">Amount Paid</p>,
            <p className="text-right">Revenue</p>,
            <p className="text-right">Profit</p>,
          ]}
          rows={rows.map((entry, idx) => {
            return {
              id: idx,
              items: [
                `${getMonthName(entry.month)} ${entry.year}`,
                <p key={`${idx}-paid-events`} className="text-right">
                  {entry?.numberOfPaidEvents}
                </p>,
                <p key={`${idx}-paid-tickets`} className="text-right">
                  {entry?.totalPaidTickets ?? 0}
                </p>,
                <p key={`${idx}-amount-processed`} className="text-right">
                  {formatToCurrency(entry?.amountProcessed ?? 0, {})}
                </p>,
                <p key={`${idx}-amount-paid`} className="text-right">
                  {formatToCurrency(entry?.totalAmountPaid ?? 0, {})}
                </p>,
                <p key={`${idx}-revenue`} className="text-right">
                  {formatToCurrency(entry?.revenue ?? 0, {})}
                </p>,
                <p key={`${idx}-profit`} className="text-right">
                  {formatToCurrency(entry?.profit ?? 0, {})}
                </p>,
              ],
            };
          })}
          rowClassName="!text-grey-900 !font-bold"
          headerClassName="!text-grey-500 !font-bold"
          loadingRows={query.limit}
          isLoading={fetchingRevenueData}
          isRowClickable={clickable}
          onRowClick={(idx) => {
            navigate(
              `/overview/revenue/${revenueData?.data?.data[idx]?.month}/${revenueData?.data?.data[idx]?.year}`,
            );
          }}
          isTableEmpty={[0, undefined].includes(revenueData?.data?.data?.length)}
          emptyTitle="No Data"
          emptySubTitle=""
          emptyImage="/assets/images/bank-glass.png"
          currentPage={query.page ?? 1}
          totalCount={revenueData?.data?.totalCount ?? 0}
          itemsPerPage={query.limit!}
          pageRangeDisplayed={3}
          onPageChange={(page) => {
            setQuery({
              ...query,
              page: page,
            });
          }}
        />
      </div>
    </div>
  );
};

const RevenueMobileMetric = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  return (
    <div className="rounded-xl bg-grey-50 p-2">
      <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-grey-500">
        {label}
      </p>
      <p className="mt-1 text-sm font-extrabold text-grey-950">{value}</p>
    </div>
  );
};
